import { AxiosResponse } from 'axios';
import { environment } from '@environment';
import { CacheAreaKey } from 'core/config/cache-config';
import { HardwareDTO } from 'core/models/product/hardware.dto';
import { PackagesDTO } from 'core/models/product/packages.dto';
import { GetSchedulesDTO, GetSubscriptionDTO } from 'core/models/product/subscription.dto';
import { EndpointHelper } from 'core/services/endpoint.helper';
import { HttpClient } from 'core/services/http';
import { StorageHelper } from 'core/services/storage';
import { SiteConfigStore } from 'core/stores/site-config.store';
import { Util } from 'utils/common.utils';
import { ContentType, UmbracoContentService } from '../umbraco/content';

export class SubscriptionService {
  private static apiBuilder = new EndpointHelper(
    `${environment.MAH_API_BASE_URL}/${environment.MAH_API_SUBS}/${environment.MAH_API_VERSION}`
  );

  private static cancelApiBuilder = new EndpointHelper(
    `${environment.MAH_API_BASE_URL}/${environment.MAH_API_CANCEL_PACKAGE}/${environment.MAH_API_VERSION}`
  );

  private static umbracoApiBuilder = new EndpointHelper(`${environment.UMBRACO_BASE_URI}`);

  static getSubscriptions(customerNumber: string, country: string): Promise<GetSubscriptionDTO> {
    const url = this.apiBuilder
      .createUrl('Subscription')
      .addQueryParam('Country', country)
      .addQueryParam('CustomerNumber', customerNumber)
      .addQueryParam('BusinessUnit', SiteConfigStore.businessUnit())
      .addQueryParam('Channel', SiteConfigStore.channel())
      .build();

    const storedItems = (StorageHelper<GetSubscriptionDTO>).GetInstance.getFromCache('req', url, CacheAreaKey.Products);
    if (storedItems) {
      return Promise.resolve(storedItems as GetSubscriptionDTO);
    }

    return HttpClient.GetInstance.get<GetSubscriptionDTO>(url).then(
      (res: AxiosResponse<GetSubscriptionDTO>) =>
        (StorageHelper<{ [key: string]: GetSubscriptionDTO }[]>).GetInstance.setCacheAndReturn(
          'req',
          CacheAreaKey.Products,
          res?.data,
          url
        ) as GetSubscriptionDTO
    );
  }

  static getSelectSubscriptions(customerNumber: string, country: string): Promise<GetSubscriptionDTO> {
    const url = this.apiBuilder
      .createUrl('Subscription')
      .addBaseRouteParam('Flex')
      .addQueryParam('Country', country)
      .addQueryParam('CustomerNumber', customerNumber)
      .addQueryParam('BusinessUnit', SiteConfigStore.businessUnit())
      .addQueryParam('Channel', SiteConfigStore.channel())
      .build();

    const storedItems = (StorageHelper<GetSubscriptionDTO>).GetInstance.getFromCache('req', url, CacheAreaKey.SelectProducts);
    if (storedItems) {
      return Promise.resolve(storedItems as GetSubscriptionDTO);
    }

    return HttpClient.GetInstance.get<GetSubscriptionDTO>(url).then(
      (res: AxiosResponse<GetSubscriptionDTO>) =>
        (StorageHelper<{ [key: string]: GetSubscriptionDTO }[]>).GetInstance.setCacheAndReturn(
          'req',
          CacheAreaKey.SelectProducts,
          res?.data,
          url
        ) as GetSubscriptionDTO
    );
  }

  static getPackageContent(): Promise<PackagesDTO[]> {
    const { platform, locale, language } = Util.GetConfigFromURL();
    const key = `Packages_${platform}_${locale}`;

    const translatedLocale = environment.LOCALISED_PACKAGES.includes(locale) ? locale : '';
    const contentType = language === 'pt' ? ContentType.PackagesPT : ContentType.Packages;

    const storedItems = (StorageHelper<PackagesDTO[]>).GetInstance.getFromCache('content', key, CacheAreaKey.Content);
    if (storedItems) {
      return Promise.resolve(storedItems as PackagesDTO[]);
    }

    return UmbracoContentService.getContent(
      contentType,
      [
        'Name',
        'ProductName',
        'ProductImage',
        'Code',
        'Price',
        'Logo',
        'PackageColor',
        'AdditionalProductImage',
        'Description',
        'ProductDetail',
        'ProductSummary',
        'SecondaryPackageColor',
        'Features',
        'IconCode',
        'SelfServiceMobileBackgroundImage',
        'SelfServiceProductCardImage',
        'SelfServiceLogoImage',
        'SelfServiceLogoImageDark',
        'SelfServiceLogoImageLight',
        'SelfServiceBackgroundImage',
        'SelfServiceProductColor',
        'SelfServiceProductHeading',
        'SelfServiceProductContent',
        'SelfServiceProductSummary',
        'SelfServiceProductDescription',
        'SelfServiceMobileBackgroundPattern',
        'SelfServiceBackgroundPattern',
        'SelfServicePromoBackgroundImage',
        'SelfServiceProductCodes',
        'SelfServiceIconCode',
        'SelfServiceProductCardCTA',
        'SelfServiceMonthlyPrice',
        'SelfServiceProductDetailCTA',
        'FeaturedChannels'
      ],
      translatedLocale
    )
      .then(
        (entities) => {
          return (StorageHelper<PackagesDTO[]>).GetInstance.setCacheAndReturn(
            'content',
            CacheAreaKey.Content,
            entities.map((entity) => entity.properties as PackagesDTO),
            key
          ) as PackagesDTO[]
        });
  }

  static getChannels(): Promise<any> {
    const url = this.umbracoApiBuilder
      .createUrl('channels')
      .addBaseRouteParam('global-site-configuration')
      .addRouteParam('ujson')
      .addQueryParam('children', 'Channels')
      .addQueryParam('select', 'Id,ChannelName,ChannelLogo,ChannelGenre')
      .addQueryParam('resolveToIds', 'ChannelGenre')
      .addQueryParam('resolveMedia', 'ChannelLogo')
      .addQueryParam('noCache', true)
      .build();

    const storedItems = (StorageHelper<any>).GetInstance.getFromCache('cnt', `Channels`, CacheAreaKey.Content);
    if (storedItems) {
      return Promise.resolve(storedItems as any);
    }

    return HttpClient.GetInstance.get<any>(url)
      .then(
        (res: AxiosResponse<any>) => (StorageHelper<any>).GetInstance.setCacheAndReturn(
          'cnt',
          CacheAreaKey.Content,
          res?.data?.entities?.map((entity: any) => entity.properties),
          `Channels`
        ) as any,
        err => {
          console.error('Failed to load content -> ', err)
          return [];
        });
  }

  static getChannelGenres(): Promise<any> {
    const url = this.umbracoApiBuilder
      .createUrl('channel-genres')
      .addBaseRouteParam('global-site-configuration')
      .addRouteParam('ujson')
      .addQueryParam('children', 'ChannelGenres')
      .addQueryParam('select', 'Id,GenreName')
      .addQueryParam('noCache', true)
      .build();

    const storedItems = (StorageHelper<any>).GetInstance.getFromCache('cnt', `ChannelGenres`, CacheAreaKey.Content);
    if (storedItems) {
      return Promise.resolve(storedItems as any);
    }

    return HttpClient.GetInstance.get<any>(url)
      .then(
        (res: AxiosResponse<any>) => (StorageHelper<any>).GetInstance.setCacheAndReturn(
          'cnt',
          CacheAreaKey.Content,
          res?.data?.entities?.map((entity: any) => entity.properties),
          `ChannelGenres`
        ) as any,
        err => {
          console.error('Failed to load content -> ', err)
          return [];
        });
  }

  static getHardwareContent(lang = Util.LangFromUrl(), locale = Util.LocaleFromUrl()): Promise<HardwareDTO[]> {
    const translatedLocale = environment.LOCALISED_HARDWARE.includes(locale) ? locale : '';
    const key = `Hardware_${translatedLocale}`;
    const storedItems = (StorageHelper<HardwareDTO[]>).GetInstance.getFromCache('content', key, CacheAreaKey.Content);
    if (storedItems) {
      return Promise.resolve(storedItems as HardwareDTO[]);
    }

    return UmbracoContentService.getContent(ContentType.Hardware, [
      'AdditionalProductImage',
      'Code',
      'Description',
      'Features',
      'IconCode',
      'Price',
      'Logo',
      'PackageColor',
      'ProductDetail',
      'ProductImage',
      'ProductName',
      'ProductSummary',
      'FeatureHeadline',
      'Specifications',
      'Capabilities',
      'Manual',
      'SelfServiceInsurancePrice',
      'SelfServiceInsurancePriceTerm',
      'SelfServiceInsuranceLabel',
      'SelfServiceMobileBackgroundImage',
      'SelfServiceProductCardImage',
      'SelfServiceLogoImage',
      'SelfServiceLogoImageLight',
      'SelfServiceBackgroundImage',
      'SelfServiceProductColor',
      'SelfServiceProductSummary',
      'SelfServiceProductHeading',
      'SelfServiceProductContent',
      'SelfServiceProductDescription',
      'SelfServiceMobileBackgroundPattern',
      'SelfServiceBackgroundPattern',
      'SelfServicePromoBackgroundImage',
      'SelfServiceProductCodes',
      'SelfServiceIconCode',
    ],
      translatedLocale).then(
        (entities) =>
          (StorageHelper<HardwareDTO[]>).GetInstance.setCacheAndReturn(
            'content',
            CacheAreaKey.Content,
            entities.map((entity) => entity.properties as HardwareDTO),
            key
          ) as HardwareDTO[]
      );
  }

  static getSafetyLandingContent(): Promise<any> {
    const storedItems = (StorageHelper<any[]>).GetInstance.getFromCache('content', `SafetyLanding`, CacheAreaKey.Content);
    if (storedItems) {
      return Promise.resolve(storedItems as any[]);
    }

    return UmbracoContentService.getContent(ContentType.Safety, [
      'Header',
      'SectionTitle',
      'Image',
      'ImageRight',
      'Body',
      'ButtonText',
      'PrimaryButton',
      'SecondaryButton'
    ]).then(
      (entities) =>
        (StorageHelper<any[]>).GetInstance.setCacheAndReturn(
          'content',
          CacheAreaKey.Content,
          entities.map((entity) => entity.properties as any),
          `SafetyLanding`
        ) as any[]
    );
  }

  static cancelPackage(customerNumber: string, country: string, subscriptionBody: {
    serviceName: string,
    agreementId: string | number,
    productId: number[],
    agreementType: string,
  }): Promise<any> {
    const url = this.cancelApiBuilder.createUrl('CancelPackages')
      .addRouteParam('cancelPackage')
      .addQueryParam('Country', `${country}`)
      .addQueryParam('BusinessUnit', SiteConfigStore.businessUnit())
      .addQueryParam('Channel', SiteConfigStore.channel())
      .addQueryParam('CustomerNumber', `${customerNumber}`)
      .addQueryParam('SwitchToStream', false)
      .build();

    return HttpClient.GetInstance.post<any, unknown>(url, subscriptionBody)
      .then(
        (res: AxiosResponse) => {
          StorageHelper.GetInstance.debounceStorageArea('req', CacheAreaKey.Products)
          return res.data;
        },
        (err) => {
          StorageHelper.GetInstance.debounceStorageArea('req', CacheAreaKey.Products)
          return Promise.reject(err)
        }
      );
  }

  static getSchedules(customerNumber: string, country: string): Promise<GetSchedulesDTO> {
    const url = this.apiBuilder
      .createUrl('Schedules')
      .addRouteParam('GetCustomerSchedules')
      .addQueryParam('Country', country)
      .addQueryParam('CustomerNumber', customerNumber)
      .addQueryParam('BusinessUnit', SiteConfigStore.businessUnit())
      .addQueryParam('Channel', SiteConfigStore.channel())
      .build();

    const storedItems = (StorageHelper<GetSchedulesDTO>).GetInstance.getFromCache('req', url, CacheAreaKey.Products);
    if (storedItems) {
      return Promise.resolve(storedItems as GetSchedulesDTO);
    }

    return HttpClient.GetInstance.get<GetSchedulesDTO>(url)
      .then((res) => StorageHelper.GetInstance.setCacheAndReturn(
        'req',
        CacheAreaKey.Products,
        res?.data,
        url
      ) as GetSchedulesDTO)
      .catch(error => {
        console.error("Get Schedule Failure");
        // NOTE: Bypass failures here to prevent dashboard load issues
        return Promise.resolve({
          schedules: [],
          action: []
        })
      });
  }

  static deleteSchedule(
    customerNumber: string,
    country: string,
    body: {
      scheduleId: number,
      productId: number,
      serviceName: string,
    }) {
    const url = this.apiBuilder
      .createUrl('Schedules')
      .addRouteParam('DeleteSchedule')
      .addQueryParam('Country', country)
      .addQueryParam('CustomerNumber', customerNumber)
      .addQueryParam('BusinessUnit', SiteConfigStore.businessUnit())
      .addQueryParam('Channel', SiteConfigStore.channel())
      .build();

    return HttpClient.GetInstance.post<typeof body, { status: string, success: string }>(url, body)
      .then((res) => {
        StorageHelper.GetInstance.debounceStorageArea('req', CacheAreaKey.Products)
        return res.data;
      });
  }
}